import React, { useEffect } from "react";
import { io } from "socket.io-client";

export const Socket = React.createContext(null);

const SocketProvider = ({ children }) => {
  const [socket, setSocket] = React.useState(null);

  useEffect(() => {
    return () => {
      if (socket?.active) {
        socket.disconnect();
      }
    };
  }, []);

  const connect = (path) => {
    if (socket?.active) {
      return socket;
    }

    // Validate the path before connecting
    if (!path || path === "undefined" || path === "null") {
      console.error("Invalid WebSocket path:", path);
      return null;
    }

    // Check if the path contains localhost (which might not have WebSocket server)
    if (path.includes("localhost")) {
      console.warn(
        "Attempting to connect to localhost - WebSocket server might not be available"
      );
    }

    console.log("Attempting to connect to WebSocket:", path);
    const connection = io(path, {
      transports: ["websocket", "polling"], // Allow fallback to polling
      timeout: 10000, // Increase timeout
      forceNew: true,
      reconnection: true, // Enable reconnection
      reconnectionAttempts: 5, // Try to reconnect 5 times
      reconnectionDelay: 1000, // Wait 1 second between attempts
      reconnectionDelayMax: 5000, // Max delay of 5 seconds
    });

    connection.on("connect", () => {
      console.log("WebSocket connected successfully");
    });

    connection.on("connect_error", (error) => {
      console.error("WebSocket connection error:", error);
      // Try to reconnect with polling if websocket fails
      if (error.message.includes("websocket")) {
        console.log("WebSocket failed, trying polling transport...");
        connection.io.opts.transports = ["polling"];
      }
    });

    connection.on("disconnect", (reason) => {
      console.log("WebSocket disconnected:", reason);
      if (reason === "io server disconnect") {
        // Server disconnected us, try to reconnect
        console.log("Server disconnected, attempting to reconnect...");
        connection.connect();
      }
    });

    connection.on("reconnect", (attemptNumber) => {
      console.log("WebSocket reconnected after", attemptNumber, "attempts");
    });

    connection.on("reconnect_error", (error) => {
      console.error("WebSocket reconnection error:", error);
    });

    connection.on("reconnect_failed", () => {
      console.error("WebSocket reconnection failed after all attempts");
    });

    setSocket(connection);
    return connection;
  };

  const disconnect = () => {
    if (socket?.active) {
      socket.disconnect();
    }
  };

  const subscribe = (topic, handler) => {
    if (socket?.active) {
      socket.on(topic, handler);
    }
  };

  const unsubscribe = (topic, handler) => {
    if (socket?.active) {
      socket.off(topic, handler);
    }
  };
  const publish = (topic, message) => {
    socket.emit(topic, message);
  };

  return (
    <Socket.Provider
      value={{ socket, connect, disconnect, subscribe, unsubscribe, publish }}
    >
      {children}
    </Socket.Provider>
  );
};

const useSocket = () => React.useContext(Socket);

export { useSocket };
export default SocketProvider;
